import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Table, Alert, Spinner,
    Modal, InputGroup,Badge
} from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency } from "../utils/formatters";
import { TVA_TAUX, CSS_TAUX } from "../utils/constantes";
import './Factures.css';
import { API_BASE_URL } from "../constants/api";

export default function NouvelleFacture() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [loadingDevis, setLoadingDevis] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [clients, setClients] = useState([]);
    const [produits, setProduits] = useState([]);
    const [showProduitModal, setShowProduitModal] = useState(false);
    
    // Toast notification
    const [toast, setToast] = useState({ show: false, message: "", type: "success" });
    
    const [numerotation, setNumerotation] = useState(null);
    const [numeroFacture, setNumeroFacture] = useState('');
    
    const queryParams = new URLSearchParams(location.search);
    const devisId = queryParams.get('devis');
    const clientId = queryParams.get('client');
    
    const [facture, setFacture] = useState({
        id_client: clientId || '',
        date_facture: new Date().toISOString().split('T')[0],
        date_echeance: '',
        remise_globale: 0,
        avance_reglement: 0,
        notes: '',
        lignes: []
    });

    const showToast = (message, type = "success") => {
        setToast({ show: true, message, type });
        setTimeout(() => {
            setToast({ show: false, message: "", type: "success" });
        }, 4000);
    };

    useEffect(() => {
        fetchClients();
        fetchProduits();
        fetchNumerotation();
        fetchTaxes();
    
        if (devisId) {
            chargerDevis(devisId);
        }
    }, [devisId]);

    const [taxes, setTaxes] = useState({
        tva_taux: 18,
        css_taux: 1,
        tva_active: true,
        css_active: true
    });

    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setTaxes(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };
  
    const fetchNumerotation = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?action=prochain_numero`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                const data = result.data;
                setNumerotation(data.config);
                
                let numero = data.config.facture_prefix;
                if (data.config.facture_annee) numero += new Date().getFullYear();
                if (data.config.facture_mois) numero += ('0' + (new Date().getMonth() + 1)).slice(-2);
                numero += data.prochain.toString().padStart(data.config.facture_chiffres, '0');
                
                setNumeroFacture(numero);
            }
        } catch (err) {
            console.error("Erreur", err);
        }
    };

    const chargerDevis = async (id) => {
        try {
            setLoadingDevis(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/devis.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement du devis");
            
            const result = await response.json();
            const devisData = result.data;
            
            if (devisData) {
                setFacture({
                    id_client: devisData.id_client,
                    date_facture: new Date().toISOString().split('T')[0],
                    date_echeance: devisData.date_echeance || '',
                    remise_globale: devisData.remise_globale || 0,
                    avance_reglement: 0,
                    notes: `Facture issue du devis ${devisData.numero_proforma}\n${devisData.notes || ''}`,
                    lignes: devisData.lignes?.map(ligne => ({
                        id_produit: ligne.id_produit,
                        reference: ligne.reference,
                        designation: ligne.designation,
                        quantite: ligne.quantite,
                        prix_unitaire_ht: ligne.prix_unitaire_ht,
                        tva_taux: ligne.tva_taux || TVA_TAUX,
                        css_taux: ligne.css_taux || CSS_TAUX,
                        remise: ligne.remise || 0
                    })) || []
                });
            }
        } catch (err) {
            console.error("Erreur chargement devis:", err);
            setError("Erreur lors du chargement du devis");
        } finally {
            setLoadingDevis(false);
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setClients(result.data || []);
                
                if (clientId && result.data) {
                    const clientExists = result.data.some(c => c.id_client == clientId);
                    if (!clientExists) {
                        setError("Le client spécifié n'existe pas");
                    }
                }
            }
        } catch (err) {
            console.error("Erreur chargement clients", err);
        }
    };

    const fetchProduits = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/produits.php?include_stock=true`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                const produitsAvecStock = (result.data || []).map(p => ({
                    ...p,
                    stock_reel: p.stock_reel || 0,
                    seuil_alerte: p.seuil_alerte || 5
                }));
                setProduits(produitsAvecStock);
            }
        } catch (err) {
            console.error("Erreur chargement produits", err);
        }
    };

    const ajouterLigne = (produit) => {
        const nouvelleLigne = {
            id_produit: produit.id_produit,
            reference: produit.reference,
            designation: produit.designation,
            quantite: 1,
            prix_unitaire_ht: produit.prix_vente_ht,
            tva_taux: taxes.tva_active ? taxes.tva_taux : 0,
            css_taux: taxes.css_active ? taxes.css_taux : 0,
            remise: 0
        };
        setFacture({
            ...facture,
            lignes: [...facture.lignes, nouvelleLigne]
        });
        setShowProduitModal(false);
        showToast("Produit ajouté avec succès", "success");
    };

    const supprimerLigne = (index) => {
        const nouvellesLignes = facture.lignes.filter((_, i) => i !== index);
        setFacture({ ...facture, lignes: nouvellesLignes });
        showToast("Produit supprimé", "info");
    };

    const updateLigne = (index, champ, valeur) => {
        const nouvellesLignes = [...facture.lignes];
        nouvellesLignes[index][champ] = valeur;
        setFacture({ ...facture, lignes: nouvellesLignes });
    };

    const calculerTotaux = () => {
        let total_ht = 0;
        let total_tva = 0;
        let total_css = 0;

        facture.lignes.forEach(ligne => {
            const montant_ligne_ht = ligne.quantite * ligne.prix_unitaire_ht * (1 - (ligne.remise || 0) / 100);
            total_ht += montant_ligne_ht;
            
            if (taxes.tva_active) {
                total_tva += montant_ligne_ht * (ligne.tva_taux || taxes.tva_taux) / 100;
            }
            if (taxes.css_active) {
                total_css += montant_ligne_ht * (ligne.css_taux || taxes.css_taux) / 100;
            }
        });

        const total_ttc = total_ht + total_tva + total_css;
        const net_a_payer = total_ttc - (facture.remise_globale || 0) - (facture.avance_reglement || 0);

        return { total_ht, total_tva, total_css, total_ttc, net_a_payer };
    };

    const totaux = calculerTotaux();

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!facture.id_client) {
            showToast("Veuillez sélectionner un client", "error");
            return;
        }
        
        if (facture.lignes.length === 0) {
            showToast("Veuillez ajouter au moins un produit", "error");
            return;
        }

        setSaving(true);

        try {
            const userStr = localStorage.getItem("user");
            if (!userStr) {
                showToast("Utilisateur non connecté", "error");
                return;
            }

            const user = JSON.parse(userStr);
            const id_utilisateur = user.id || user.id_utilisateur;
            
            if (!id_utilisateur) {
                showToast("ID utilisateur non trouvé", "error");
                return;
            }

            const token = localStorage.getItem("token");
            
            const payload = {
                action: 'create',
                ...facture,
                numero_facture: numeroFacture,
                id_utilisateur: id_utilisateur
            };
            
            if (devisId) {
                payload.id_proforma = devisId;
            }

            const response = await fetch(
                `${API_BASE_URL}/factures.php`,
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                }
            );

            const result = await response.json();

            if (result.success) {
                showToast("Facture créée avec succès !", "success");
                setTimeout(() => {
                    navigate('/ventes/factures/liste');
                }, 2000);
            } else {
                showToast(result.message || "Erreur lors de la création", "error");
            }
        } catch (err) {
            console.error("Erreur:", err);
            showToast("Erreur de connexion au serveur", "error");
        } finally {
            setSaving(false);
        }
    };

    if (loadingDevis) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <div className="loading-animation mb-4">
                                <div className="loading-ring"><div className="loading-ring-inner"></div></div>
                                <i className="ri-file-list-3-line loading-icon"></i>
                            </div>
                            <h5 className="fw-bold text-primary mb-2">Chargement du devis</h5>
                            <p className="text-muted small mb-0">Récupération des données...</p>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-animation { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; background: linear-gradient(135deg, #21ACCD, #1a8ba8); animation: pulse-ring 1.5s ease-in-out infinite; }
                    .loading-ring-inner { position: absolute; top: 5px; left: 5px; right: 5px; bottom: 5px; border-radius: 50%; background: white; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: #21ACCD; z-index: 2; animation: pulse-icon 1.5s ease-in-out infinite; }
                    @keyframes pulse-ring { 0% { transform: scale(0.9); opacity: 0.7; } 50% { transform: scale(1.1); opacity: 0.3; } 100% { transform: scale(0.9); opacity: 0.7; } }
                    @keyframes pulse-icon { 0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; } 50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.8; } }
                `}</style>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                
                {/* Toast Notification */}
                {toast.show && (
                    <div className="toast-notification toast-notification-show">
                        <div className={`toast-content toast-${toast.type}`}>
                            <div className="toast-icon">
                                {toast.type === 'success' ? (
                                    <i className="ri-checkbox-circle-line"></i>
                                ) : toast.type === 'error' ? (
                                    <i className="ri-error-warning-line"></i>
                                ) : (
                                    <i className="ri-information-line"></i>
                                )}
                            </div>
                            <div className="toast-message">
                                <h6>{toast.type === 'success' ? 'Succès' : toast.type === 'error' ? 'Erreur' : 'Information'}</h6>
                                <p>{toast.message}</p>
                            </div>
                            <button className="toast-close" onClick={() => setToast({ show: false, message: "", type: "success" })}>
                                <i className="ri-close-line"></i>
                            </button>
                        </div>
                        <div className="toast-progress"></div>
                    </div>
                )}

                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                            <i className="ri-file-list-3-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                        </div>
                                        <div>
                                            <h4 className="main-title mb-0 fw-bold">
                                                {devisId ? 'Convertir devis en facture' : 'Créer une facture'}
                                            </h4>
                                            <p className="text-muted small mb-0">
                                                {devisId ? 'Les données du devis ont été pré-remplies' : 'Remplissez les informations pour créer une nouvelle facture'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <Button 
                                        variant="outline-secondary" 
                                        as={Link} 
                                        to="/ventes/factures/liste"
                                        className="rounded-pill px-3"
                                    >
                                        <i className="ri-arrow-left-line me-2"></i>
                                        Retour à la liste
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Form onSubmit={handleSubmit}>
                        <Row>
                            {/* Informations générales */}
                            <Col lg={8}>
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-information-line me-2"></i>
                                            Informations générales
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Numéro de facture</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={numeroFacture}
                                                        disabled
                                                        readOnly
                                                        className="bg-light border-2"
                                                    />
                                                    <Form.Text className="text-muted">
                                                        Généré automatiquement selon la configuration
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Client <span className="text-danger">*</span></Form.Label>
                                                    <Form.Select
                                                        value={facture.id_client}
                                                        onChange={(e) => setFacture({...facture, id_client: e.target.value})}
                                                        required
                                                        className="border-2"
                                                    >
                                                        <option value="">Sélectionner un client</option>
                                                        {clients.map(client => (
                                                            <option key={client.id_client} value={client.id_client}>
                                                                {client.raison_sociale} ({client.code_client})
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Date facture</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={facture.date_facture}
                                                        onChange={(e) => setFacture({...facture, date_facture: e.target.value})}
                                                        required
                                                        className="border-2"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Date échéance</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={facture.date_echeance}
                                                        onChange={(e) => setFacture({...facture, date_echeance: e.target.value})}
                                                        required
                                                        className="border-2"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Notes</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={facture.notes}
                                                        onChange={(e) => setFacture({...facture, notes: e.target.value})}
                                                        placeholder="Notes ou observations..."
                                                        className="border-2"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Lignes de facture */}
                                <Card className="border-0 shadow-sm rounded-3">
                                    <Card.Header className="py-3 d-flex justify-content-between align-items-center" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-shopping-cart-line me-2"></i>
                                            Produits / Services
                                        </h5>
                                        <Button 
                                            variant="light" 
                                            size="sm"
                                            onClick={() => setShowProduitModal(true)}
                                            className="rounded-pill"
                                        >
                                            <i className="ri-add-line me-1"></i>
                                            Ajouter un produit
                                        </Button>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        {facture.lignes.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table className="align-middle mb-0">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th className="ps-4">Produit</th>
                                                            <th className="text-center" style={{ width: '100px' }}>Qté</th>
                                                            <th className="text-end" style={{ width: '120px' }}>Prix HT</th>
                                                            <th className="text-center" style={{ width: '100px' }}>Remise %</th>
                                                            <th className="text-end" style={{ width: '120px' }}>Total HT</th>
                                                            <th className="text-center" style={{ width: '80px' }}>TVA</th>
                                                            <th className="text-center" style={{ width: '50px' }}></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {facture.lignes.map((ligne, index) => (
                                                            <tr key={index}>
                                                                <td className="ps-4">
                                                                    <div className="fw-semibold">{ligne.designation}</div>
                                                                    <small className="text-muted">{ligne.reference}</small>
                                                                </td>
                                                                <td className="text-center">
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="1"
                                                                        value={ligne.quantite}
                                                                        onChange={(e) => updateLigne(index, 'quantite', parseInt(e.target.value) || 1)}
                                                                        size="sm"
                                                                        className="text-center"
                                                                    />
                                                                </td>
                                                                <td className="text-end">
                                                                    <Form.Control
                                                                        type="number"
                                                                        step="0.01"
                                                                        value={ligne.prix_unitaire_ht}
                                                                        onChange={(e) => updateLigne(index, 'prix_unitaire_ht', parseFloat(e.target.value) || 0)}
                                                                        size="sm"
                                                                        className="text-end"
                                                                    />
                                                                </td>
                                                                <td className="text-center">
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="0"
                                                                        max="100"
                                                                        value={ligne.remise || 0}
                                                                        onChange={(e) => updateLigne(index, 'remise', parseFloat(e.target.value) || 0)}
                                                                        size="sm"
                                                                        className="text-center"
                                                                    />
                                                                </td>
                                                                <td className="text-end fw-bold">
                                                                    {formatCurrency(ligne.quantite * ligne.prix_unitaire_ht * (1 - (ligne.remise || 0)/100))}
                                                                </td>
                                                                <td className="text-center">
                                                                    <Badge bg="light" text="dark" className="rounded-pill">
                                                                        {ligne.tva_taux || (taxes.tva_active ? taxes.tva_taux : 0)}%
                                                                    </Badge>
                                                                </td>
                                                                <td className="text-center">
                                                                    <Button 
                                                                        variant="link" 
                                                                        className="text-danger p-0"
                                                                        onClick={() => supprimerLigne(index)}
                                                                    >
                                                                        <i className="ri-delete-bin-line fs-16"></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <i className="ri-shopping-cart-line fs-48 text-muted mb-3 d-block"></i>
                                                <p className="text-muted mb-3">Aucun produit ajouté</p>
                                                <Button 
                                                    variant="outline-primary"
                                                    onClick={() => setShowProduitModal(true)}
                                                    className="rounded-pill px-4"
                                                    style={{ borderColor: '#21ACCD', color: '#21ACCD' }}
                                                >
                                                    <i className="ri-add-line me-2"></i>
                                                    Ajouter un produit
                                                </Button>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Récapitulatif */}
                            <Col lg={4}>
                                <Card className="border-0 shadow-sm rounded-3 sticky-top" style={{ top: '20px' }}>
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-calculator-line me-2"></i>
                                            Récapitulatif
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div className="recap-item d-flex justify-content-between mb-3 pb-2 border-bottom">
                                            <span className="text-muted">Total HT</span>
                                            <span className="fw-bold">{formatCurrency(totaux.total_ht)}</span>
                                        </div>
                                        <div className="recap-item d-flex justify-content-between mb-3 pb-2 border-bottom">
                                            <span className="text-muted">TVA {taxes.tva_active ? `(${taxes.tva_taux}%)` : '(inactive)'}</span>
                                            <span className="fw-bold text-warning">{formatCurrency(totaux.total_tva)}</span>
                                        </div>
                                        <div className="recap-item d-flex justify-content-between mb-3 pb-2 border-bottom">
                                            <span className="text-muted">CSS {taxes.css_active ? `(${taxes.css_taux}%)` : '(inactive)'}</span>
                                            <span className="fw-bold text-info">{formatCurrency(totaux.total_css)}</span>
                                        </div>
                                        
                                        <div className="recap-item d-flex justify-content-between mb-4 pt-2">
                                            <span className="fw-bold fs-6">Total TTC</span>
                                            <span className="fw-bold fs-5" style={{ color: '#21ACCD' }}>{formatCurrency(totaux.total_ttc)}</span>
                                        </div>

                                        <div className="bg-light p-3 rounded-3 mb-4">
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold">Remise globale (FCFA)</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    value={facture.remise_globale}
                                                    onChange={(e) => setFacture({...facture, remise_globale: parseFloat(e.target.value) || 0})}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                            <Form.Group className="mb-0">
                                                <Form.Label className="fw-semibold">Avance / Acompte</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    value={facture.avance_reglement}
                                                    onChange={(e) => setFacture({...facture, avance_reglement: parseFloat(e.target.value) || 0})}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </div>

                                        <div className="recap-item d-flex justify-content-between mb-4 pt-2 border-top pt-3">
                                            <span className="h5 fw-bold">Net à payer</span>
                                            <span className="h5 fw-bold text-success">{formatCurrency(totaux.net_a_payer)}</span>
                                        </div>

                                        <div className="d-grid gap-2">
                                            <Button 
                                                type="submit" 
                                                variant="primary" 
                                                size="lg"
                                                disabled={saving}
                                                className="rounded-pill py-2"
                                                style={{ background: '#21ACCD', border: 'none' }}
                                            >
                                                {saving ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" />
                                                        Création...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="ri-save-line me-2"></i>
                                                        {devisId ? 'Créer la facture' : 'Créer la facture'}
                                                    </>
                                                )}
                                            </Button>
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => navigate('/ventes/factures/liste')}
                                                className="rounded-pill"
                                            >
                                                Annuler
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </div>
                <Footer />
            </div>

            {/* Modal de sélection des produits */}
            <Modal show={showProduitModal} onHide={() => setShowProduitModal(false)} size="lg" centered className="product-modal">
                <Modal.Header closeButton className="border-0 pt-4 px-4" style={{ background: '#21ACCD' }}>
                    <Modal.Title className="text-white">
                        <i className="ri-shopping-cart-line me-2"></i>
                        Sélectionner un produit
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 pb-4">
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4">Référence</th>
                                    <th>Désignation</th>
                                    <th className="text-end">Prix HT</th>
                                    <th className="text-center">Stock</th>
                                    <th className="pe-4"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {produits.map(produit => (
                                    <tr key={produit.id_produit}>
                                        <td className="ps-4"><span className="reference-badge">{produit.reference}</span></td>
                                        <td className="fw-semibold">{produit.designation}</td>
                                        <td className="text-end">{formatCurrency(produit.prix_vente_ht)}</td>
                                        <td className="text-center">
                                            <span className={produit.stock_reel < produit.seuil_alerte ? 'text-danger fw-bold' : 'text-muted'}>
                                                {produit.stock_reel || 0}
                                            </span>
                                        </td>
                                        <td className="pe-4">
                                            <Button 
                                                size="sm" 
                                                variant="primary"
                                                onClick={() => ajouterLigne(produit)}
                                                className="rounded-circle"
                                                style={{ background: '#21ACCD', border: 'none', width: '32px', height: '32px' }}
                                            >
                                                <i className="ri-add-line"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4">
                    <Button variant="outline-secondary" onClick={() => setShowProduitModal(false)} className="rounded-pill px-4">
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                }
                .recap-item {
                    transition: all 0.2s;
                }
                .sticky-top {
                    position: sticky;
                    top: 20px;
                    z-index: 100;
                }
                .fs-16 {
                    font-size: 1rem;
                }
                
                /* Toast Notifications */
                .toast-notification {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    z-index: 9999;
                    min-width: 350px;
                    max-width: 450px;
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.15);
                    overflow: hidden;
                    transform: translateX(120%);
                    transition: transform 0.3s ease-out;
                    opacity: 0;
                }
                .toast-notification-show {
                    transform: translateX(0);
                    opacity: 1;
                }
                .toast-content {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    padding: 16px 20px;
                    position: relative;
                }
                .toast-success { border-left: 4px solid #10b981; }
                .toast-error { border-left: 4px solid #ef4444; }
                .toast-info { border-left: 4px solid #0dcaf0; }
                .toast-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                }
                .toast-success .toast-icon { background: #ecfdf5; color: #10b981; }
                .toast-error .toast-icon { background: #fef2f2; color: #ef4444; }
                .toast-info .toast-icon { background: #e2f3f5; color: #0dcaf0; }
                .toast-message { flex: 1; }
                .toast-message h6 { margin: 0 0 4px 0; font-size: 0.9rem; font-weight: 600; }
                .toast-message p { margin: 0; font-size: 0.8rem; color: #64748b; }
                .toast-close {
                    background: transparent;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    padding: 5px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: color 0.2s;
                }
                .toast-close:hover { color: #1e293b; }
                .toast-progress {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    height: 3px;
                    width: 100%;
                    background: rgba(0,0,0,0.05);
                    animation: progress 4s linear forwards;
                }
                .toast-success .toast-progress { background: linear-gradient(90deg, #10b981, #059669); }
                .toast-error .toast-progress { background: linear-gradient(90deg, #ef4444, #dc2626); }
                .toast-info .toast-progress { background: linear-gradient(90deg, #0dcaf0, #0ea5e9); }
                @keyframes progress {
                    0% { width: 100%; }
                    100% { width: 0%; }
                }
                @media (max-width: 768px) {
                    .toast-notification {
                        top: 10px;
                        right: 10px;
                        left: 10px;
                        min-width: auto;
                        max-width: none;
                    }
                }
                
                .product-modal .modal-content {
                    border-radius: 24px;
                    overflow: hidden;
                }
                .rounded-3 {
                    border-radius: 12px !important;
                }
            `}</style>
        </React.Fragment>
    );
}